import {
  Component,
  computed,
  effect,
  input,
  inject,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatTableDataSource,
} from '@angular/material/table';
import { STATUS_TO_ICON } from '../variables';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ModificationSetting, TableEditMode } from '../models/tool.models';
import { FormDialComponent } from '../dialogues/form-dial/form-dial.component';
import { PaginationOptions, PaginationRequest } from '../models/pagination.models';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '../pipes/date.pipe';
import { ApplyFunctionPipe } from '../pipes/apply-function.pipe';
import { TruncatePipe } from '../pipes/truncate.pipe';
import { selectionManager } from '../selection';
import {
  ButtonDirective,
  ConfirmationDialogComponent,
  CuiDialogService,
} from '@innersource/common-ui-components';

export type ColumnType = 'text' | 'number' | 'date' | 'predefined' | 'textArea' | 'option' | 'bool';

export type TableColumn = {
  id: string;
  label: string;
  type: ColumnType;
  isModifiable: boolean;
  isOptional?: boolean;
  options?: string[];
};

const defaultPageSizeOptions = [5, 10, 20, 50, 100];

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  imports: [
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatCellDef,
    MatCell,
    MatButton,
    NgTemplateOutlet,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    CdkTextareaAutosize,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    RouterLink,
    ButtonDirective,
    MatIcon,
    MatTooltip,
    MatIconButton,
    DatePipe,
    ApplyFunctionPipe,
    TruncatePipe,
  ],
})
export class DynamicTableComponent {
  /** Inject services. */
  private readonly dialogRef = inject(MatDialog);
  private readonly confirmationDialog = inject(CuiDialogService);

  readonly data = input.required<Record<string, unknown>[]>();

  // All columns shown in the table
  // Does not have to align with the objects keys. However, each TableColumn ID must fit an object key.
  readonly columns = input.required<Array<TableColumn>>();

  // Optional title for the table. Will be placed above the table if set otherwise only the table is shown
  readonly tableTitle = input<string | undefined>('');

  readonly allowEdit = input(false);

  paginationOptions = input<PaginationOptions>();

  readonly pageChanged = output<PaginationRequest>();

  readonly modificationSettings = input<Partial<ModificationSetting>>({
    allowNew: false,
    allowEdit: false,
    allowDelete: false,
  });

  readonly allowSelection = input(false);

  /** Whether selecting a row keeps other selected rows selected. */
  readonly allowMultiSelect = input(true);

  readonly editType = input<TableEditMode>('table');

  readonly selectionChanged = output<Record<string, unknown>[]>();

  readonly paginator = viewChild(MatPaginator);

  readonly tableData = computed(() => {
    const data = new MatTableDataSource([...this.list.options()]);
    if (!this.paginationOptions()) data.paginator = this.paginator();
    return data;
  });

  readonly list = selectionManager<Record<string, unknown>>([], []);
  readonly editSelection = signal<Record<string, unknown> | undefined>(undefined);

  /** Whether a row is currently being edited inline. */
  readonly isEditing = computed(
    () => this.editType() === 'table' && this.editSelection() !== undefined
  );

  readonly showFullTextSelection = signal<Record<string, unknown> | undefined>(undefined);

  displayedColumns = computed(() => {
    const columns = this.columns().map(c => c.id);

    // Add edit column to UI if set
    const modificationSettings = this.modificationSettings();
    if (this.allowEdit() && (modificationSettings.allowEdit || modificationSettings.allowDelete)) {
      columns.push('actions');
    }

    // Add select column to UI if set
    if (this.allowSelection()) {
      columns.unshift('select');
    }

    return columns;
  });

  readonly pageSizeOptions = signal(defaultPageSizeOptions);
  readonly showPaginator = signal(true);

  // Pagination Settings
  readonly totalRecords = signal(0);
  readonly pageIndex = computed(() => (this.paginationOptions()?.page ?? 1) - 1);
  readonly pageSize = computed(() => this.paginationOptions()?.perPage ?? 5);
  // Limit of characters to be displayed
  readonly lengthLimit = 200;

  constructor() {
    effect(() => {
      const data = this.data();
      const options = untracked(this.paginationOptions);
      this.list.setOptions(data);
      this.totalRecords.set(options?.total ?? data.length);
      this.setPageSizeOption(options?.total ?? data.length);
    });

    effect(() => {
      this.selectionChanged.emit(this.list.selected());
    });
  }

  /** Normalizes an option column value to a list of selected options. */
  toArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map(item => this.stringifyOptionValue(item));
    }
    if (value === undefined || value === null || value === '') {
      return [];
    }
    return [this.stringifyOptionValue(value)];
  }

  /**
   * Will return nested element value based on a given string
   *
   * @param nestedColName string of the nested col,
   * separated with "." e.g. "test.object.id"
   * @param element object containing the keys
   * @param fallback fallback value if the key does not exist
   */
  getNestedElement(
    nestedColName: string,
    element: Record<string, unknown>,
    fallback: unknown = undefined
  ) {
    const nestedKeys = nestedColName.split('.');

    let current: unknown = element;

    for (const key of nestedKeys) {
      if (typeof current === 'object' && current !== null && key in current) {
        current = (current as Record<string, unknown>)[key];
      } else {
        return fallback;
      }
    }
    return current;
  }

  loadIconNameFromStatus(status: string) {
    if (Object.keys(STATUS_TO_ICON).includes(status)) {
      return STATUS_TO_ICON[status as keyof typeof STATUS_TO_ICON].iconName;
    }
    return STATUS_TO_ICON.Fallback.iconName;
  }

  loadIconColorFromStatus(status: string) {
    if (Object.keys(STATUS_TO_ICON).includes(status)) {
      return STATUS_TO_ICON[status as keyof typeof STATUS_TO_ICON].iconColor;
    }
    return STATUS_TO_ICON.Fallback.iconColor;
  }

  /**
   * Configure pageSizeOption, if table is too small hide paginator
   * @param dataLength
   */
  setPageSizeOption(dataLength: number) {
    if (dataLength <= defaultPageSizeOptions[0]) {
      this.showPaginator.set(false);
    } else {
      this.showPaginator.set(true);
      const sizes = defaultPageSizeOptions.filter(num => num < dataLength);
      sizes.push(dataLength);
      this.pageSizeOptions.set(sizes);
    }
  }

  /** Selects element on row click if not in edit mode **/
  handleRowClick(event: Event, rowIndex: number) {
    event.stopPropagation();

    const element = this.list.options().at(rowIndex);
    if (element && this.allowSelection() && this.editSelection() !== element) {
      if (this.allowMultiSelect()) {
        this.list.toggle(element);
        return;
      }

      this.list.set(this.list.isSelected(element) ? [] : [element]);
    }
  }

  /** Stop Propagation if element is collapsable **/
  handleShowFullText(event: Event, element: Record<string, never>, columnId: string) {
    const el = this.getNestedElement(columnId, element);
    if ((el as string).length > this.lengthLimit) {
      this.showFullTextSelection.set(element);
      event.stopPropagation();
    }
  }

  /** Remove Item from list **/
  handleDelete(row: Record<string, unknown>) {
    this.confirmationDialog.open(ConfirmationDialogComponent, {
      size: 'small',
      ariaLabel: 'Delete Entry',
      data: {
        confirmTitle: 'Delete Entry',
        confirmMessage: 'Do you really want to delete this entry?',
        onConfirm: () => this.list.removeOption(row),
      },
    });
  }

  /** Open Dialogue to edit or add an entry **/
  private openEditPopup(row: Record<string, unknown>, newItem = false) {
    const formDef = this.columns().map(col => {
      const value = col.id.includes('.') ? this.getNestedElement(col.id, row) : row[col.id];

      // Only add `value` if it exists (not undefined or null)
      if (value !== undefined && value !== null) {
        return { ...col, value };
      }

      // Return column unchanged if no value
      return { ...col };
    });

    const dialogRef = this.dialogRef.open(FormDialComponent, {
      data: {
        title: newItem ? 'Add Entry' : 'Edit Entry',
        description: newItem
          ? 'Please insert the information and confirm to add a new entry.'
          : 'Please modify the listed information and click confirm. Cancel to ignore the changes.',
        formDef: formDef,
      },
    });

    // Add new item to the datasource if it's not empty
    dialogRef.afterClosed().subscribe((response: Record<string, unknown> | undefined) => {
      if (response && !this.isEmpty(response)) {
        const newObject = { ...row };
        for (const key of Object.keys(response)) {
          newObject[key] = response[key];
        }
        if (newItem) {
          this.list.addOption(newObject);
        } else {
          this.list.updateOption(row, newObject);
        }
      }
    });
  }

  isEmpty(row: Record<string, unknown>) {
    return this.columns()
      .filter(col => !col.isOptional) // only non-optional columns
      .every(col => {
        const value = row[col.id];
        if (value === '' || value === null || value === undefined) {
          return true;
        }
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return false;
      });
  }

  /**
   * Handle click on e button of row.
   */
  handleEdit(row: Record<string, unknown>) {
    if (this.editType() == 'popup') {
      this.openEditPopup(row);
    } else {
      const currentSelection = this.editSelection();

      if (currentSelection?.['isNew'] && this.isEmpty(currentSelection)) {
        this.list.removeOption(currentSelection);
      }

      this.editSelection.set(currentSelection === row ? undefined : row);
    }
  }

  /** Add new item to list **/
  addItem() {
    const newObj: Record<string, unknown> = {};
    newObj['isNew'] = true;
    this.columns().forEach(col => {
      if (col.type === 'bool') {
        newObj[col.id] = false;
      } else if (col.type === 'option') {
        newObj[col.id] = [];
      } else {
        newObj[col.id] = '';
      }
    });
    if (this.editType() == 'popup') {
      this.openEditPopup(newObj, true);
    } else {
      const currSelected = this.editSelection();
      if (!currSelected || !(currSelected['isNew'] && this.isEmpty(currSelected))) {
        this.editSelection.set(newObj);
        this.list.addOption(newObj);
        this.setPageSizeOption(this.list.options().length);
      }
    }
  }

  pageChangeEvent(event: PageEvent) {
    this.pageChanged.emit({
      page: event.pageIndex + 1,
      perPage: event.pageSize,
    });
  }

  /** Converts option values to readable strings without object defaults. */
  private stringifyOptionValue(value: unknown): string {
    switch (typeof value) {
      case 'object':
        return JSON.stringify(value) ?? '';
      case 'string':
        return value;
      case 'number':
        return Number.prototype.toString.call(value);
      case 'bigint':
        return BigInt.prototype.toString.call(value);
      case 'boolean':
        return value ? 'true' : 'false';
      case 'symbol':
        return Symbol.prototype.toString.call(value);
      case 'function':
        return Function.prototype.toString.call(value);
      default:
        return 'undefined';
    }
  }
}
